import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSupabaseClient } from '../providers/supabase';
import { useHtClient } from '../providers/ht';

export interface Lead {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string | null;
  sf_account?: string | null;
  quiz?: number | null;
  priorities?: any | null;
  created_at: string;
}

export interface LeadCreate {
  first_name: string;
  last_name: string;
  email: string;
  phone?: string | null;
  sf_account?: string | null;
  quiz?: number | null;
  priorities?: any | null;
  // Advisor-portal attribution + deeper-onboarding intake (optional; back-compat
  // with the walk-in flow). advisor_public_id comes from ?advisor=<public_id> on an
  // advisor-sent link; onboarding is the 17-answer intake map (86badfke7).
  advisor_public_id?: string | null;
  onboarding?: Record<string, string> | null;
}

export interface LeadUpdate {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string | null;
  sf_account?: string | null;
  quiz?: number | null;
  priorities?: any | null;
}

export interface LeadPrioritiesUpdate {
  priorities: any;
}

// Lead enriched with quiz archetype + captured demographic info, for the
// admin lead-reporting view (MONEY-7). Backend joins lead -> quiz and reads
// through the service-key admin client.
export interface LeadDetailed extends Lead {
  archetype: string | null;
  info: Record<string, string>;
}

// Get all leads, optionally filtered by SF account
export const useLeads = (sfAccount?: string, options?: { enabled?: boolean }) => {
  const { data: client } = useHtClient();

  return useQuery({
    queryKey: ['leads', sfAccount],
    queryFn: async () => {
      if (!client) throw new Error("API client not initialized");

      const url = sfAccount ? `/leads?sf_account=${sfAccount}` : '/leads';
      const response = await client.get(url);
      return response.data;
    },
    enabled: options?.enabled !== false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
};

// Get all leads enriched with archetype + demographics for admin reporting.
export const useLeadsDetailed = (sfAccount?: string, options?: { enabled?: boolean }) => {
  const { data: client } = useHtClient();

  return useQuery({
    queryKey: ['leads-detailed', sfAccount],
    queryFn: async (): Promise<LeadDetailed[]> => {
      if (!client) throw new Error("API client not initialized");

      const url = sfAccount ? `/leads/detailed?sf_account=${sfAccount}` : '/leads/detailed';
      const response = await client.get(url);
      return response.data;
    },
    enabled: options?.enabled !== false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
};

// Get a specific lead by ID
export const useLead = (leadId: number) => {
  const { data: client } = useHtClient();
  
  return useQuery({
    queryKey: ['lead', leadId],
    queryFn: async () => {
      if (!client) throw new Error("API client not initialized");
      
      const response = await client.get(`/leads/${leadId}`);
      return response.data;
    },
    enabled: !!leadId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
};

// Create a new lead
export const useCreateLead = () => {
  const { data: client } = useHtClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (leadData: LeadCreate) => {
      if (!client) throw new Error("API client not initialized");
      
      const response = await client.post('/leads', leadData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
    }
  });
};

// Update a lead
export const useUpdateLead = () => {
  const { data: client } = useHtClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ leadId, leadData }: { leadId: number; leadData: LeadUpdate }) => {
      if (!client) throw new Error("API client not initialized");
      
      const response = await client.put(`/leads/${leadId}`, leadData);
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['lead', variables.leadId] });
      queryClient.invalidateQueries({ queryKey: ['leads'] });
    }
  });
};

// Update lead priorities by quiz ID
export const useUpdateLeadPrioritiesByQuiz = () => {
  const { data: client } = useHtClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ quizId, priorities }: { quizId: number; priorities: any }) => {
      if (!client) throw new Error("API client not initialized");
      
      const response = await client.put(`/leads/quiz/${quizId}/priorities`, { priorities });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
    }
  });
};

// Delete a lead
export const useDeleteLead = () => {
  const { data: client } = useHtClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (leadId: number) => {
      if (!client) throw new Error("API client not initialized");
      
      const response = await client.delete(`/leads/${leadId}`);
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['lead', variables] });
      queryClient.invalidateQueries({ queryKey: ['leads'] });
    }
  });
}; 